import { GetServerSideProps } from "next";

import * as artistService from "@/server/services/artists";
import * as collectionService from "@/server/services/collections";
import { logger } from "server/lib/logger";

import ArtistPage, { Props } from "@/client/components/pages/Artist";
import type MetadataHead from "@/client/components/common/MetdataHead";
import { getArtistCanonicalUrl } from "@/client/lib/links";
import { isAddress as isWalletAddressFormat } from "@/shared/eth";

export const getServerSideProps: GetServerSideProps = async (context) => {
  let artistIdOrSlug = context?.query?.id as string;
  if (
    !artistIdOrSlug ||
    artistIdOrSlug === "undefined" ||
    artistIdOrSlug === "null"
  ) {
    return {
      notFound: true,
    };
  }

  try {
    const artist = await readArtist(artistIdOrSlug);

    // if we don't have an artist throw an error
    if (!artist) {
      return {
        notFound: true,
      };
    }

    // if the ID used on the page is not the slug of the artist then redirect to the slug
    if (artist.slug && artist.slug !== artistIdOrSlug) {
      return {
        redirect: {
          destination: `/artists/${artist.slug}`,
          permanent: false,
        },
      };
    }

    // build up page metadata
    const metadata: MetadataHead = {};
    if (artist?.name) metadata.title = artist?.name + " - Artist | Art Blocks";
    if (artist?.avatarUrl) metadata.imageUrl = artist?.avatarUrl;
    if (artist?.bio) metadata.description = artist?.bio;
    metadata.canonicalUrl = getArtistCanonicalUrl({
      id: artist._id as string,
      slug: artist.slug,
    });

    const props: Props = {
      artist: artist ? JSON.parse(JSON.stringify(artist)) : null,
      metadata,
    };

    return {
      props,
    };
  } catch (err) {
    logger.error(err, "failed to fetch artist in getServerSideProps");
    throw err;
  }
};

const readArtist = async (artistIdOrSlugOrAddress: string) => {
  // if the ID is a contract address see if we have an artist with associated wallet address
  if (isWalletAddressFormat(artistIdOrSlugOrAddress)) {
    const artist = await artistService.readByWalletAddress(
      artistIdOrSlugOrAddress
    );
    if (artist) return artist;

    // if there is no artist then lets check to see if any collections have a matching artist address
    // currently Artist only supports a single `artistWalletAddress` but an artist could have multiple across collections
    const collection = await collectionService.list({
      artistWalletAddress: artistIdOrSlugOrAddress,
      limit: 1,
    });
    // find the artist by the artist ID set on the collection
    if (collection?.length && collection[0].artistIds?.length) {
      return artistService.readById(collection[0].artistIds[0]);
    }
  }

  // otherwise lookup by ID or slug
  return artistService.readByIdOrSlug(artistIdOrSlugOrAddress);
};

export default ArtistPage;
